import { Box, TextField } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import debounce from "lodash.debounce";

const TextInput = ({
  label,
  name,
  style = {},
  value,
  onChange,
  error,
  helperText,
  placeholder = "",
  typeLabel = false,
  showLabel = true,
  borderClr = "#EBEBEB",
  errorBorderClr = "#f44336",
}) => {
  const [inputValue, setInputValue] = useState(value || "");

  const handleChange = (event) => {
    setInputValue(event.target.value);
  };

  const debouncedOnChange = useCallback(
    debounce((fieldName, fieldValue) => {
      onChange && onChange(fieldName, fieldValue);
    }, 300),
    [onChange]
  );

  useEffect(() => {
    debouncedOnChange(name, inputValue);
    return () => {
      debouncedOnChange.cancel();
    };
  }, [inputValue, debouncedOnChange, name]);

  return (
    <Box sx={{ width: "100%" }}>
      <TextField
        id={name}
        name={name}
        placeholder={placeholder}
        variant="outlined"
        fullWidth
        value={inputValue}
        onChange={handleChange}
        error={Boolean(error)}
        helperText={error ? helperText : ""}
        disabled={typeLabel}
        sx={{
          "& .MuiFormHelperText-root": {
            color: "text.secondary",
            "&.Mui-error": { color: "text.secondary" },
          },
          "& .MuiOutlinedInput-root": {
            height: "40px",
            borderRadius: "8px",
            backgroundColor: "#FDFDFD",
            boxSizing: "border-box",

            // Lock border to 1px in ALL states.
            // MUI default focused state uses 2px which visually pushes
            // the fieldset outside the 40px height — this prevents that.
            "& fieldset": {
              borderColor: borderClr,
              borderWidth: "1px !important",
            },
            "&:hover fieldset": {
              borderColor: borderClr,
              borderWidth: "1px !important",
            },
            "&.Mui-focused fieldset": {
              borderColor: borderClr,
              borderWidth: "1px !important",
            },
            "&.Mui-error fieldset": {
              borderColor: borderClr,
              borderWidth: "1px !important",
            },
            "&.Mui-error:hover fieldset": {
              borderColor: borderClr,
              borderWidth: "1px !important",
            },

            // Lock input height and padding so it sits cleanly inside 40px
            "& input": {
              height: "40px",
              boxSizing: "border-box",
              padding: "0 14px",
              outline: "none",
              backgroundColor: "transparent",
              "&:-webkit-autofill": {
                WebkitBoxShadow: "0 0 0px 1000px #FDFDFD inset !important",
                WebkitTextFillColor: "inherit !important",
                borderRadius: "8px",
              },
              "&:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active": {
                WebkitBoxShadow: "0 0 0px 1000px #FDFDFD inset !important",
              },
            },
          },
          ...style,
        }}
      />
    </Box>
  );
};

export default TextInput;
